import { useState } from 'react';
import { Flex, Text, Anchor, Button } from '@mantine/core';
import { IconFile, IconCircleCheck, IconExternalLink } from '@tabler/icons-react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import type { EnrichedExternalTransaction } from '@dealstack/frontend/app/services/DocumentExtraction.service';
import { DocumentViewerModalV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DEAL_ROUTES, PEOPLE_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { TransactionDetailsGrid } from './TransactionDetailsGrid';

interface Props {
	transaction: EnrichedExternalTransaction;
	onMerge?: () => void;
	onDiscard?: () => void;
	isMerged?: boolean;
}

export const TransactionDetailPanel = ({ transaction, onMerge, onDiscard, isMerged }: Props) => {
	const tCapManage = useTranslationCapManage();
	const navigate = useNavigate();
	const { dealId } = useParams<{ dealId: string }>();
	const [viewerOpen, setViewerOpen] = useState(false);

	const fileExtension = transaction.fileName?.split('.').pop()?.toLowerCase() || undefined;
	const hasFile = !!transaction.fileUrl;
	const issuerPersonId = transaction.inferredIssuerPersonId ?? null;
	const canNavigateToPersonTransactions = !!isMerged && !!dealId && !!issuerPersonId;
	const openViewer = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		setViewerOpen(true);
	};
	const goToPersonTransactions = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!dealId || !issuerPersonId) {
			return;
		}
		const path = generatePath(
			`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.TRANSACTIONS}`,
			{ dealId, personId: issuerPersonId }
		);
		navigate(path);
	};

	return (
		<Flex direction="column" gap={16} py={16} px={24}>
			<TransactionDetailsGrid
				transaction={transaction}
				hasFile={hasFile}
				onViewInDocument={openViewer}
			/>

			<Flex
				align="center"
				justify="space-between"
				py={12}
				px={16}
				style={{ backgroundColor: '#f8f9fa', borderRadius: 6 }}
			>
				<Flex align="center" gap="sm" style={{ flex: 1 }}>
					<IconFile size={20} color="#868e96" />
					<Text size="sm">{transaction.fileName}</Text>
				</Flex>
				{transaction.fileUrl && (
					<Anchor
						size="sm"
						component="button"
						onClick={(e: React.MouseEvent) => {
							e.stopPropagation();
							setViewerOpen(true);
						}}
					>
						{tCapManage('batch_analysis.view')}
					</Anchor>
				)}
			</Flex>

			<Flex align="center" justify="flex-end" gap="md">
				{isMerged ? (
					<>
						<Text size="sm" color="green" fw={500}>
							{tCapManage('batch_analysis.merged_into_dealstack')}
						</Text>
						{canNavigateToPersonTransactions && (
							<Button
								variant="subtle"
								leftSection={<IconExternalLink size={16} />}
								onClick={goToPersonTransactions}
							>
								{tCapManage('batch_analysis.see_in_transactions')}
							</Button>
						)}
					</>
				) : (
					<>
						{onDiscard && (
							<Button
								variant="subtle"
								color="red"
								onClick={(e: React.MouseEvent) => {
									e.stopPropagation();
									onDiscard();
								}}
							>
								{tCapManage('batch_analysis.discard')}
							</Button>
						)}
						{onMerge && (
							<Button
								leftSection={<IconCircleCheck size={16} />}
								onClick={(e: React.MouseEvent) => {
									e.stopPropagation();
									onMerge();
								}}
							>
								{tCapManage('batch_analysis.merge_into_dealstack')}
							</Button>
						)}
					</>
				)}
			</Flex>

			{transaction.fileUrl && (
				<DocumentViewerModalV2
					opened={viewerOpen}
					onClose={() => setViewerOpen(false)}
					file={transaction.fileUrl}
					fileName={transaction.fileName}
					fileExtension={fileExtension}
					isLoading={false}
				/>
			)}
		</Flex>
	);
};
